import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getAuth, signInWithCustomToken, onAuthStateChanged, signOut } from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// Firebase Authentication
export const auth = getAuth(app);

// GD-004: Replaced anonymous auth with custom token auth.
// Staff must log in via the server-side /api/v1/staff/login endpoint
// which returns a Firebase custom token with hotel/restaurant claims.
// Firestore rules enforce access based on these claims.

// API base URL for staff login endpoint
const API_URL = import.meta.env.VITE_MECONCIERGE_API_URL || '';

/**
 * Sign in with a custom token obtained from the staff login API.
 * The token includes claims { hotelId, restaurantId, staffId, role }
 * which Firestore rules use for hotel-level access control.
 */
export async function signInWithToken(token: string): Promise<void> {
  await signInWithCustomToken(auth, token);
}

/**
 * Sign out the current user and clear Firebase auth state.
 */
export async function firebaseSignOut(): Promise<void> {
  await signOut(auth);
}

/**
 * Check if a user is currently authenticated with Firebase.
 * Returns a promise that resolves when auth state is known.
 */
export function waitForAuth(): Promise<boolean> {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(!!user);
    });
  });
}

/**
 * Call the server-side staff login API.
 * Returns the custom token and staff data on success.
 */
export async function staffLoginApi(params: {
  email: string;
  password: string;
  hotelId?: string;
  restaurantId?: string;
}): Promise<{
  success: boolean;
  token?: string;
  staff?: {
    id: string;
    name: string;
    email: string;
    role: string;
    hotelId: string;
    restaurantId: string;
    restaurantName: string;
  };
  error?: string;
}> {
  try {
    const response = await fetch(`${API_URL}/api/v1/staff/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    const data = await response.json();

    if (data.success && data.data) {
      return {
        success: true,
        token: data.data.token,
        staff: data.data.staff,
      };
    }

    return {
      success: false,
      error: data.error?.message || 'Invalid email or password',
    };
  } catch {
    return {
      success: false,
      error: 'Unable to connect to authentication server',
    };
  }
}

// Use the hotel-db named database (meconcierge project)
// Multi-tenancy is achieved through collection prefixes (hotels/{hotelId}/...)
export const db = getFirestore(app, "hotel-db");
export const storage = getStorage(app);

// For backward compatibility - returns the default database
// Multi-tenancy is handled via collection paths, not separate named databases
export const getFirestoreForDatabase = (databaseId: string) => {
  // Always return the default database
  // The databaseId is stored as reference for routing/data organization
  return db;
};

// Helper to get the collection path for a hotel
// If hotelId is 'default' or empty, returns the base collection name
// Otherwise returns hotels/{hotelId}/{collectionName}
export const getHotelCollectionPath = (hotelId: string, collectionName: string) => {
  if (!hotelId || hotelId === 'default' || hotelId === 'template') {
    return collectionName;
  }
  return `hotels/${hotelId}/${collectionName}`;
};

export default app;
